import { createSelector } from '@opengov/compose-app/reselect';
import { ISelectOptionsBlock } from '../../../src/components/common/SelectWithChildren/SelectWithChildren';
import { COMBOS_OF_CALENDAR_UNITS, CalendarUnitCombo } from '../../../src/utils/calendar';
import {
  selectAccountTypeMapForCoaColumnsFn,
  selectCoaDimensionColumnsFn,
  selectReportCoaDimensionFn,
  selectReportCoaDimensionMetadataFn,
  selectReportCoaJoinColNameFn
} from '@opengov/reporting-transforms/dimensions';
import {
  IReportCoaDimensionMetadata,
  IReportSegment,
  isReportLedger,
  CalendarUnit,
  dateColumnNameToCalendarUnit
} from '@opengov/reporting-types';
import {
  EColumnDimensionType,
  EReportColumnTypes,
  IDimensionalDateReportColumn,
  IDimensionalReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import {
  selectConsistentCoAEnabledFlag,
  selectIncludeLabelsInAccountNumberAndEbtoDimensionsFlag
} from '../../common/flags';
import { sortLedgerTypes } from '../../common/ledgerType';
import { reportLocaleText } from '../../common/locale';
import {
  selectCurrentReportConfiguration,
  selectDimensionalQueryAccountType,
  selectDimensionalSummaryQueries
} from '../../common/reportConfiguration/selectors';
import { ISegmentOption } from '../AccountCodeFiltersContainer/AccountCodeFilters';
import { reportExplorerSelector } from '../state/reportExplorerState';

export const selectReportDatasetLens = createSelector(reportExplorerSelector, (state) => {
  if (!state) return;
  return state.reportDatasetLenses?.length > 0 && state.reportDatasetLenses[0];
});

export const selectAllReportDatasetLenses = createSelector(reportExplorerSelector, (state) => {
  if (!state) return;
  return state.reportDatasetLenses;
});

export const selectIsReportDatasetLensStandard = createSelector(selectReportDatasetLens, (lens) =>
  Boolean(lens && !!lens.standardType)
);

export const selectLensDateColumns = createSelector(
  selectReportDatasetLens,
  (lens) =>
    lens && lens.columns && lens.columns.filter((column) => column.type && column.type.includes('/logical/date'))
);

export const selectLensYearColumns = createSelector(
  selectReportDatasetLens,
  (lens) =>
    lens &&
    lens.columns &&
    lens.columns.filter((column) => column.semanticTypes.includes('urn:opengov:types/semantic/calendar/fiscal/year'))
);

export const selectReportCoaDimension = createSelector(selectReportDatasetLens, selectReportCoaDimensionFn);

export const selectReportCoaDimensionId = createSelector(selectReportCoaDimension, (coaDimension) => {
  return coaDimension && coaDimension.id;
});

export const selectReportCoaJoinColName = createSelector(
  selectReportDatasetLens,
  selectReportCoaDimension,
  selectReportCoaJoinColNameFn
);

export const selectCoaDimensionColumns = createSelector(
  selectReportCoaDimension,
  selectReportCoaJoinColName,
  selectIncludeLabelsInAccountNumberAndEbtoDimensionsFlag,
  selectConsistentCoAEnabledFlag,
  selectCoaDimensionColumnsFn
);

// Since the date dimension is now based on the entity fiscal start, there should only ever be one
export const selectReportDateDimension = createSelector(
  selectReportDatasetLens,
  (lens) => lens && lens.dimensions && lens.dimensions.filter((dimension) => dimension.id.includes('/date/'))[0]
);

export const selectReportCoaDimensionMetadata = createSelector(
  selectReportCoaDimension,
  selectReportCoaJoinColName,
  selectReportCoaDimensionMetadataFn
);

export const selectDimensionalAccountTypes = createSelector(
  selectReportCoaDimensionMetadata,
  (coaDimensionMetadata) => coaDimensionMetadata && coaDimensionMetadata.ledgerTypes
);

export const selectAccountTypeMapForCoaColumns = createSelector(
  selectDimensionalAccountTypes,
  selectAccountTypeMapForCoaColumnsFn
);

export const selectCoaShowAccountTypeOptions = createSelector(selectDimensionalAccountTypes, (accountTypes) => {
  const options =
    accountTypes &&
    sortLedgerTypes(accountTypes).map((ledgerType) => {
      const { label, filterValue } = ledgerType;
      return { label, value: filterValue };
    });
  return [<ISelectOptionsBlock>{ options: options }];
});

interface ILabelValuePair {
  label: string;
  value: string;
}

interface IIndexedNestedLabelValue extends ILabelValuePair {
  tooltip?: string;
  disabled?: boolean;
  children: ILabelValuePair[];
  index: number;
}

function convertSegmentsToOptions(segments: IReportSegment[]): IIndexedNestedLabelValue[] {
  return segments.map((segment) => {
    const children = segment.categoryLevels.map(({ label, columnRef }) => ({ label, value: columnRef }));

    if (isReportLedger(segment)) {
      children.push({
        label: segment.accountCodeColumnLabel,
        value: segment.accountCodeColumnRef
      });
    } else {
      children.push({
        label: segment.accountCodeColumnLabel,
        value: segment.descriptionColumnRef
      });
    }
    return { index: segment.index, label: segment.label, value: segment.label, children };
  });
}

function getAllSegments(coaDimensionMetadata: IReportCoaDimensionMetadata): IReportSegment[] {
  const allSegments = coaDimensionMetadata.segments.concat(coaDimensionMetadata.ledgerTypes);
  return allSegments.sort((a, b) => a.index - b.index);
}

function keepSegmentsAndSelectedLedgerSegment(segments: IReportSegment[], selectedLedger: string) {
  return segments.filter((segment) => (isReportLedger(segment) ? segment.filterValue === selectedLedger : segment));
}

export interface IColumnControlLabels {
  object: string;
  reportExplorer: { controls: { columnGroupDisabledToolTipText: string; segment: string } };
}

export const coaSegmentsForCoaChartControlsForLedgerType = (
  coaDimensionMetadata: IReportCoaDimensionMetadata,
  currentLedgerType: string,
  localeText: IColumnControlLabels,
  disableObjectSegments
) => {
  const segments = getAllSegments(coaDimensionMetadata);
  const segmentsAndOneLedger = keepSegmentsAndSelectedLedgerSegment(segments, currentLedgerType);
  const segmentOptions = convertSegmentsToOptions(segmentsAndOneLedger);

  // Replace with a disabled placeholder
  if (disableObjectSegments) {
    const { index: ledgerIndexValue } = segments.find((segment) =>
      isReportLedger(segment) ? segment.filterValue === currentLedgerType : false
    );
    const ledgerOptionIndex = segmentOptions.findIndex((segment) => segment.index === ledgerIndexValue);
    segmentOptions[ledgerOptionIndex] = {
      label: localeText.object,
      index: ledgerIndexValue,
      value: undefined,
      disabled: true,
      tooltip: localeText.reportExplorer.controls.columnGroupDisabledToolTipText,
      children: undefined
    };
  }

  return {
    label: localeText.reportExplorer.controls.segment,
    options: segmentOptions
  };
};

export const selectAccountTypesForAllAccounts = createSelector(
  selectDimensionalSummaryQueries,
  (dimensionSummaryQueries) =>
    dimensionSummaryQueries &&
    dimensionSummaryQueries.map((query) => query.dimension && query.dimension.showAccountType)
);

export const selectAllAccountTypeEquality = createSelector(
  selectAccountTypesForAllAccounts,
  (allAccountTypes) => (receivedAccountTypes?: string[]) => {
    const comparator = receivedAccountTypes || allAccountTypes;
    if (comparator.length > 1) {
      const filteredAccountNames = comparator.filter((accountType) => ![undefined, null].includes(accountType));
      return filteredAccountNames.every((accountType) => accountType === filteredAccountNames[0]);
    }
    return true;
  }
);

const COA_DIMENSION_METADATA_NULL_OBJECT = {
  joinColumnName: '',
  dimensionId: '',
  segments: [],
  ledgerTypes: []
};

export const selectCoaSegmentsForCoaColumnsControls = createSelector(
  selectReportCoaDimensionMetadata,
  selectDimensionalQueryAccountType,
  selectAllAccountTypeEquality,
  reportLocaleText,
  (
    coaDimensionMetadata: IReportCoaDimensionMetadata | undefined,
    currentLedgerType,
    allAccountEquality,
    localeText: IColumnControlLabels
  ) => {
    const allAccountsAreSame = allAccountEquality();
    return coaSegmentsForCoaChartControlsForLedgerType(
      coaDimensionMetadata ?? COA_DIMENSION_METADATA_NULL_OBJECT,
      currentLedgerType,
      localeText,
      !allAccountsAreSame
    );
  }
);

export const selectCoaSegmentsForCoaRowsControlsForLedgerType = createSelector(
  selectReportCoaDimensionMetadata,
  reportLocaleText,
  (coaDimensionMetadata: IReportCoaDimensionMetadata | undefined, localeText: IColumnControlLabels) =>
    (ledgerType: string) => {
      return [
        coaSegmentsForCoaChartControlsForLedgerType(
          coaDimensionMetadata ?? COA_DIMENSION_METADATA_NULL_OBJECT,
          ledgerType,
          localeText,
          false
        )
      ];
    }
);

export const selectCoaRowsControls = createSelector(selectCoaSegmentsForCoaColumnsControls, (segments) => [segments]);

function getAccountCodeFilterSegmentOptions(
  coaDimensionMetadata: IReportCoaDimensionMetadata,
  currentLedgerType: string
): ISegmentOption[] {
  const segments = getAllSegments(coaDimensionMetadata);
  const segmentsAndOneLedger = keepSegmentsAndSelectedLedgerSegment(segments, currentLedgerType);

  return segmentsAndOneLedger.map((segment) => {
    return { label: segment.label, id: segment.accountCodeColumnRef, index: segment.index };
  });
}

export const selectAccountCodeFiltersSegmentOptions = createSelector(
  selectReportCoaDimensionMetadata,
  selectDimensionalQueryAccountType,
  (
    coaDimensionMetadata: IReportCoaDimensionMetadata | undefined,
    dimensionQueryAccountType: string
  ): ISegmentOption[] => {
    return getAccountCodeFilterSegmentOptions(
      coaDimensionMetadata ?? COA_DIMENSION_METADATA_NULL_OBJECT,
      dimensionQueryAccountType
    );
  }
);

export const selectIndividualAccountCodeFiltersSegmentOptions = createSelector(
  selectReportCoaDimensionMetadata,
  (coaDimensionMetadata) => (currentLedgerType: string) =>
    getAccountCodeFilterSegmentOptions(coaDimensionMetadata ?? COA_DIMENSION_METADATA_NULL_OBJECT, currentLedgerType)
);

export const selectDateOptionsForCoaColumnsControls = createSelector(
  selectLensDateColumns,
  selectLensYearColumns,
  reportLocaleText,
  (dateColumns, yearColumns, localeText) => {
    let dateOptions = [];

    if (dateColumns && dateColumns.length > 0) {
      // For each date column, create an array of options with the correct children for that semantic type
      dateOptions = dateColumns.map((column) => {
        const dateColumnOptions = [];
        const calendarUnits = COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FullDate];

        calendarUnits.forEach((calendarUnit) => {
          return dateColumnOptions.push({
            label: localeText.calendarUnits[calendarUnit],
            value: calendarUnit
          });
        });

        return {
          label: column.label,
          value: column.name,
          children: dateColumnOptions
        };
      });
    }

    if (yearColumns && yearColumns.length > 0) {
      dateOptions = dateOptions.concat(
        yearColumns.map((column) => {
          return {
            label: column.label,
            value: column.name,
            children: [
              {
                label: localeText.calendarUnitsSingular[CalendarUnit.FiscalYear],
                value: CalendarUnit.FiscalYear
              }
            ]
          };
        })
      );
    }

    return { label: localeText.reportExplorer.controls.date, options: dateOptions };
  }
);

export const selectCoaColumnsControls = createSelector(
  selectCoaSegmentsForCoaColumnsControls,
  selectDateOptionsForCoaColumnsControls,
  (segments, dateOptions) => {
    // Only show date options if they exist
    if (dateOptions && dateOptions.options && dateOptions.options.length > 0) {
      return [dateOptions, segments];
    }
    return [segments];
  }
);

export const selectDimensionalRowsColumn = createSelector(selectCurrentReportConfiguration, (reportConfig) => {
  return (
    reportConfig &&
    reportConfig.dimensionalSummaryQueries[0] &&
    reportConfig.dimensionalSummaryQueries[0].dimension.rowsColumn
  );
});

export const selectRowsColumnControlFormat = createSelector(
  selectDimensionalRowsColumn,
  selectCoaRowsControls,
  (rowsColumn, rowsControls): [string, string] => {
    const parentLevel =
      rowsControls &&
      rowsControls[0] &&
      rowsControls[0].options.find(
        (option) =>
          option.children &&
          option.children.find((child) => child.value === (rowsColumn && rowsColumn.name)) !== undefined
      );
    return [parentLevel && parentLevel.value, rowsColumn && rowsColumn.name];
  }
);

export const selectColumnsColumnControlFormat = createSelector(
  selectCurrentReportConfiguration,
  selectCoaColumnsControls,
  (reportConfig, columnsControls): [string, string] => {
    const columnsColumn =
      reportConfig &&
      reportConfig.dimensionalSummaryQueries[0] &&
      reportConfig.dimensionalSummaryQueries[0].dimension.columnsColumn;
    if (
      columnsColumn &&
      (columnsColumn as IDimensionalReportColumn).dimensionType === EColumnDimensionType.DATE &&
      (columnsColumn as IDimensionalDateReportColumn).dateColumnName
    ) {
      const calendarUnit = dateColumnNameToCalendarUnit[(columnsColumn as IDimensionalDateReportColumn).dateColumnName];
      return [columnsColumn.name, `${calendarUnit}`];
    } else if (columnsColumn && columnsColumn.columnType === EReportColumnTypes.storage) {
      return [columnsColumn.name, CalendarUnit.FiscalYear];
    } else {
      // segments will always be last in the array
      const segmentOptions = columnsControls[columnsControls.length - 1];
      const parentLevel =
        segmentOptions &&
        segmentOptions.options.find(
          (option) =>
            option.children &&
            option.children.find((child) => child.value === (columnsColumn && columnsColumn.name)) !== undefined
        );
      return [parentLevel && parentLevel.value, columnsColumn && columnsColumn.name];
    }
  }
);

export const selectColumnsColumnControlDisplayName = createSelector(
  selectCurrentReportConfiguration,
  selectCoaColumnsControls,
  reportLocaleText,
  (reportConfig, columnsControls, locale): string => {
    const columnsColumn =
      reportConfig &&
      reportConfig.dimensionalSummaryQueries[0] &&
      reportConfig.dimensionalSummaryQueries[0].dimension.columnsColumn;
    if (!columnsColumn) return;
    if (
      (columnsColumn as IDimensionalReportColumn).dimensionType === EColumnDimensionType.DATE &&
      (columnsColumn as IDimensionalDateReportColumn).dateColumnName
    ) {
      const calendarUnit = dateColumnNameToCalendarUnit[(columnsColumn as IDimensionalDateReportColumn).dateColumnName];
      let calendarUnitLabel;

      for (let i = 0; i < columnsControls.length; i++) {
        if (columnsControls[i].options) {
          const matchedColumnOption = columnsControls[i].options.find((option) => option.value === columnsColumn.name);
          if (matchedColumnOption) {
            matchedColumnOption.children.forEach((child) => {
              if (child.value === calendarUnit) {
                calendarUnitLabel = child.label;
              }
            });
          }
          if (calendarUnitLabel) {
            break;
          }
        }
      }
      return `${columnsColumn.displayName}: ${calendarUnitLabel}`;
    } else if (columnsColumn.columnType === EReportColumnTypes.storage) {
      return `${columnsColumn.displayName}: ${locale.calendarUnits.fiscalYear}`;
    } else {
      let selectedLabel = '';
      columnsControls.find((columnsControlsOption) => {
        return !!columnsControlsOption.options.find((option) => {
          return !!option.children.find((child) => {
            if (child.value === columnsColumn.name) {
              selectedLabel = child.label;
              return true;
            }
          });
        });
      });
      if (!columnsColumn.displayName) {
        return selectedLabel || locale.reportTable.codes;
      }
      return columnsColumn.displayName;
    }
  }
);
